/**
 * DebateQuotaDialog — Mur d'achat one-shot déclenché par un 402
 * { error: "debate_quota_exceeded", price_cents, is_intro, checkout_path, ... }
 * renvoyé par POST /api/chat ou POST /api/chat/:id/relaunch.
 *
 * Le prix et le chemin de checkout sont TOUJOURS lus dans le payload 402,
 * jamais hardcodés.
 */
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { apiFetch, ApiError } from "@/lib/api";

export interface DebateQuotaPayload {
  error: "debate_quota_exceeded";
  price_cents: number;
  is_intro?: boolean;
  checkout_path: string;
  plan?: string;
  used?: number;
  limit?: number;
}

export function isDebateQuotaPayload(p: unknown): p is DebateQuotaPayload {
  if (!p || typeof p !== "object") return false;
  const o = p as Record<string, unknown>;
  return (
    o.error === "debate_quota_exceeded" &&
    typeof o.price_cents === "number" &&
    typeof o.checkout_path === "string"
  );
}

/** Extract a 402 quota payload from a thrown error, or null. */
export function extractQuotaPayload(err: unknown): DebateQuotaPayload | null {
  if (err instanceof ApiError && err.status === 402 && isDebateQuotaPayload(err.payload)) {
    return err.payload;
  }
  return null;
}

function formatEur(cents: number): string {
  const v = cents / 100;
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: Number.isInteger(v) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(v);
}

interface Props {
  payload: DebateQuotaPayload | null;
  onClose: () => void;
  /**
   * Hook synchrone appelé juste avant la redirection vers Stripe.
   * Permet aux appelants de stasher leur contexte (ex. convId + question
   * en cours) pour le restituer au retour sur ?checkout=success.
   */
  onBeforeRedirect?: () => void;
}

export function DebateQuotaDialog({ payload, onClose, onBeforeRedirect }: Props) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const open = payload != null;
  const price = payload ? formatEur(payload.price_cents) : "";

  async function handleBuy() {
    if (!payload) return;
    setLoading(true);
    try {
      const res = await apiFetch<{ checkout_url?: string; url?: string }>(
        payload.checkout_path,
        { method: "POST", body: JSON.stringify({}) },
      );
      const url = res.checkout_url ?? res.url;
      if (!url) {
        toast.error("Le paiement est momentanément indisponible, réessayez dans un instant.");
        return;
      }
      try { onBeforeRedirect?.(); } catch { /* ignore */ }
      window.location.href = url;
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 401) {
          navigate({ to: "/login" });
          return;
        }
        if (e.status === 503) {
          toast.error("Le paiement est momentanément indisponible, réessayez dans un instant.");
          return;
        }
      }
      toast.error("Impossible d'ouvrir le paiement. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ajouter un débat</DialogTitle>
          <DialogDescription>
            Vous avez utilisé votre débat offert. Pour soumettre cette saisine au Conseil,
            ajoutez un débat à {price}.
          </DialogDescription>
        </DialogHeader>

        {/* Le tarif de lancement n'existe plus — le back renvoie is_intro: false. */}

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--qv3-grey-500, #8a8580)",
              fontFamily: "var(--qv3-font-mono, ui-monospace, monospace)",
              fontSize: 12,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              cursor: loading ? "not-allowed" : "pointer",
              padding: "8px 14px",
            }}
          >
            Plus tard
          </button>
          <button
            type="button"
            onClick={handleBuy}
            disabled={loading}
            style={{
              background: "#0a0a0a",
              color: "#f3efe6",
              border: "1px solid #FF1744",
              padding: "12px 22px",
              fontFamily: "var(--qv3-font-mono, ui-monospace, monospace)",
              fontSize: 12,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              cursor: loading ? "wait" : "pointer",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Redirection…" : `Ajouter un débat — ${price}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DebateQuotaDialog;
